
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Calendar, SlidersHorizontal, X } from "lucide-react";

interface FilterValues {
  search: string;
  category: string;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  presenter: string;
  minRating: string;
}

interface FeedbackFiltersProps {
  filters: FilterValues;
  onFilterChange: (filters: FilterValues) => void;
  categories: string[];
  presenters: string[];
}

const FeedbackFilters: React.FC<FeedbackFiltersProps> = ({
  filters,
  onFilterChange,
  categories,
  presenters
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [localFilters, setLocalFilters] = React.useState<FilterValues>(filters);
  
  const handleFilterChange = (key: keyof FilterValues, value: any) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };
  
  const applyFilters = () => {
    onFilterChange(localFilters);
  };
  
  const resetFilters = () => {
    const newFilters = {
      search: "",
      category: "all_categories",
      dateFrom: undefined,
      dateTo: undefined,
      presenter: "all_presenters",
      minRating: "any_rating"
    };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };
  
  const activeFilterCount = Object.values(filters).filter(value => 
    value !== "" && value !== undefined && value !== "all_categories" && value !== "all_presenters" && value !== "any_rating"
  ).length;

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle>Filters</CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-8"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            {activeFilterCount > 0 ? `${activeFilterCount} active` : "Filter"}
          </Button>
        </div>
        {activeFilterCount > 0 && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={resetFilters}
            className="text-destructive hover:text-destructive/80 h-7 px-2 mt-1"
          >
            <X className="h-3.5 w-3.5 mr-1" />
            Clear all
          </Button>
        )}
      </CardHeader>
      
      {isExpanded && (
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Search</label>
              <Input
                placeholder="Search by course title"
                value={localFilters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Category</label>
              <Select
                value={localFilters.category}
                onValueChange={(value) => handleFilterChange("category", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_categories">All categories</SelectItem>
                  {categories.map(category => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Presenter</label>
              <Select
                value={localFilters.presenter}
                onValueChange={(value) => handleFilterChange("presenter", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All presenters" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_presenters">All presenters</SelectItem>
                  {presenters.map(presenter => (
                    <SelectItem key={presenter} value={presenter}>{presenter}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Min. Rating</label>
              <Select
                value={localFilters.minRating}
                onValueChange={(value) => handleFilterChange("minRating", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any_rating">Any rating</SelectItem>
                  <SelectItem value="1">1+ star</SelectItem>
                  <SelectItem value="2">2+ stars</SelectItem>
                  <SelectItem value="3">3+ stars</SelectItem>
                  <SelectItem value="4">4 stars</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">From Date</label>
              <DatePicker
                date={localFilters.dateFrom}
                setDate={(date) => handleFilterChange("dateFrom", date)}
                className="w-full"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">To Date</label>
              <DatePicker
                date={localFilters.dateTo}
                setDate={(date) => handleFilterChange("dateTo", date)}
                className="w-full"
              />
            </div>
          </div>
          
          <div className="flex justify-end mt-4 gap-2">
            <Button variant="outline" onClick={resetFilters}>Reset</Button>
            <Button onClick={applyFilters}>Apply Filters</Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default FeedbackFilters;
